/**
 * SidebarToggleContext · v2.7.65+E141 (2026-02-03)
 * ═════════════════════════════════════════════════════════════════════
 * State for the Admin Console sidebar's three visibility modes:
 *   • mobileOpen        — hamburger drawer on < lg viewports.
 *   • desktopCollapsed  — Phase-3 arrow toggle: slides the desktop sidebar
 *                          off-screen so console content reflows to full
 *                          width. Persists in localStorage.
 *   • microMode         — v2.7.65+E141 · Icon-only micro-rail mode. When
 *                          true, the sidebar shrinks to a ~72 px vertical
 *                          icon strip (labels hidden, footer avatar-only,
 *                          section headers collapse to divider). Persists.
 *
 * Default no-op values keep things safe for components rendered
 * OUTSIDE the provider (login, portal).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const DESKTOP_STORAGE_KEY = "admin-sidebar-desktop-collapsed-v1";
const MICRO_STORAGE_KEY   = "admin-sidebar-micro-v1";

export const SidebarToggleContext = createContext({
  mobileOpen: false,
  setMobileOpen: () => {},
  toggleMobile: () => {},
  desktopCollapsed: false,
  setDesktopCollapsed: () => {},
  toggleDesktop: () => {},
  microMode: false,
  setMicroMode: () => {},
  toggleMicro: () => {},
});

export function SidebarToggleProvider({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(DESKTOP_STORAGE_KEY) === "1"; }
    catch { return false; }
  });
  const [microMode, setMicroMode] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(MICRO_STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(DESKTOP_STORAGE_KEY, desktopCollapsed ? "1" : "0"); }
    catch { /* private mode */ }
  }, [desktopCollapsed]);
  useEffect(() => {
    try { window.localStorage.setItem(MICRO_STORAGE_KEY, microMode ? "1" : "0"); }
    catch { /* private mode */ }
  }, [microMode]);

  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);
  const toggleDesktop = useCallback(() => setDesktopCollapsed((v) => !v), []);
  const toggleMicro = useCallback(() => setMicroMode((v) => !v), []);

  const value = useMemo(
    () => ({
      mobileOpen, setMobileOpen, toggleMobile,
      desktopCollapsed, setDesktopCollapsed, toggleDesktop,
      microMode, setMicroMode, toggleMicro,
    }),
    [mobileOpen, toggleMobile, desktopCollapsed, toggleDesktop, microMode, toggleMicro],
  );
  return <SidebarToggleContext.Provider value={value}>{children}</SidebarToggleContext.Provider>;
}

export function useSidebarToggle() {
  return useContext(SidebarToggleContext);
}
